import clsx from 'clsx';
import React, { FC, HTMLProps } from 'react'

interface CustomInputTextProps extends HTMLProps<HTMLInputElement> {
    label?: string;
    isError: boolean;
    errorMessage?: string;
    containerClassName?: string;
    suffix?: string;
    preffix?: string;
    allowNegative?: boolean;
}

const CustomInputText: FC<CustomInputTextProps> = ({
    label,
    isError,
    errorMessage,
    required,
    className,
    containerClassName,
    suffix,
    preffix,
    type = "text",
    allowNegative = false,
    ...props
}) => {
    const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key
        // Always allow control/navigation keys
        const ctrlKeys = [
            'Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','Home','End'
        ]
        if (ctrlKeys.includes(key) || (e.ctrlKey || e.metaKey)) return

        // Block scientific notation and plus
        if (['e','E','+'].includes(key)) {
            e.preventDefault()
            return
        }

        // Allow a single leading minus when enabled
        if (key === '-') {
            if (!allowNegative) {
                e.preventDefault()
                return
            }
            const input = e.currentTarget
            const alreadyHasMinus = input.value.includes('-')
            const atStart = (input.selectionStart ?? 0) === 0
            const hasSelectionCoveringStart = (input.selectionStart ?? 0) === 0 && (input.selectionEnd ?? 0) > 0
            if (alreadyHasMinus || (!atStart && !hasSelectionCoveringStart)) {
                e.preventDefault()
            }
            return
        }

        // Allow decimal point only once
        if (key === '.') {
            const input = e.currentTarget
            const hasDot = input.value.includes('.')
            if (hasDot) {
                e.preventDefault()
            }
            return
        }

        // Allow digits only
        if (!/^[0-9]$/.test(key)) {
            e.preventDefault()
        }
    }
    return (
        <div className={containerClassName}>
            {label && (
                <label className='flex-fill form-label fs-6 text-black'>
                    {label}
                    {required && <span className='text-danger'>*</span>}
                </label>
            )}
            <div className='position-relative d-flex'>
                <input
                    className={clsx("form-control form-control-lg form-control-solid", { "invalid-form": isError }, { "ps-16": preffix }, { "pe-16": suffix }, className)}
                    style={{ borderRadius: '8px' }}
                    type={allowNegative && type === 'number' ? 'text' : type}
                    {...(type === "number" ? { onKeyDown: handleNumberKeyDown, inputMode: 'decimal' } : {})}
                    {...props}
                />

                {suffix && <div className='suffix h-44px w-60px position-absolute end-0 z-index-1 d-flex align-items-center justify-content-center'>
                    {suffix}
                </div>}

                {preffix && <div className='preffix h-44px w-60px position-absolute start-0 z-index-1 d-flex align-items-center justify-content-center'>
                    {preffix}
                </div>}
            </div>
            {isError && (
                <div
                    data-test-id='error-validation-alert-of-name'
                    className='fv-plugins-message-container text-danger mt-2'
                >
                    <span role='alert'>{errorMessage}</span>
                </div>
            )}
        </div>
    )
}

export default CustomInputText